import React, { useEffect, useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Moon } from "lucide-react";
import { motion } from "framer-motion";
import { getUserSettings } from "@/lib/userSettings";
import { useTranslation } from "@/hooks/useTranslation";
import { useInputMethod } from "@/contexts/InputMethodContext";
import { useExternalSwitchInput } from "@/hooks/useExternalSwitchInput";
import { getEyeControlAction } from "@/hooks/useEyeControlInput";
import { cn } from "@/lib/utils";

type WakeBehavior = 'dwell' | 'select' | 'voice' | 'pointer';

function getWakeBehavior(controlMethod: string): WakeBehavior {
  switch (controlMethod) {
    case 'standard_pointer':
      return 'pointer';
    case 'gaze_tracking':
    case 'head_tracking':
      return 'dwell';
    case 'eye_control':
    case 'external_switch':
    case 'mouth_gestures':
      return 'select';
    case 'voice':
      return 'voice';
    default:
      return 'pointer';
  }
}

export default function Rest() {
  const navigate = useNavigate();
  const settings = getUserSettings();
  const { t } = useTranslation();
  const { cursorPosition } = useInputMethod();
  
  const wakeBehavior = getWakeBehavior(settings.controlMethod || 'standard_pointer');
  
  const [selectCount, setSelectCount] = useState(0);
  const [voiceCount, setVoiceCount] = useState(0);
  const [isDwelling, setIsDwelling] = useState(false);
  const [localDwellProgress, setLocalDwellProgress] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const wakeTargetRef = useRef<HTMLDivElement>(null);
  const dwellStartRef = useRef<number>(0);
  
  const [voiceNumber] = useState(() => Math.floor(Math.random() * 9) + 1);
  
  const effectiveCursorPosition = settings.controlMethod === 'standard_pointer' 
    ? mousePosition 
    : cursorPosition;

  const handleWake = useCallback(() => {
    // Always navigate to home rather than navigate(-1), which could land on a
    // settings page, help screen, or any other page the user visited before rest.
    navigate("/", { replace: true });
  }, [navigate]);

  // Handle external switch "select" action directly for Rest wake-up
  const handleSwitchSelect = useCallback(() => {
    if (wakeBehavior !== 'select') return;
    setSelectCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 3) {
        handleWake();
      }
      return newCount;
    });
  }, [wakeBehavior, handleWake]);

  useExternalSwitchInput({
    onSelect: handleSwitchSelect,
    enabled: wakeBehavior === 'select',
  });

  // Track mouse position for standard pointer dwell
  useEffect(() => {
    if (wakeBehavior !== 'dwell') return;
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [wakeBehavior]);

  // DWELL WAKE BEHAVIOR
  useEffect(() => {
    if (wakeBehavior !== 'dwell') return;
    
    const checkDwell = () => {
      if (!wakeTargetRef.current) return;
      
      const rect = wakeTargetRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const radius = rect.width / 2;
      
      const distance = Math.sqrt(
        Math.pow(effectiveCursorPosition.x - centerX, 2) + 
        Math.pow(effectiveCursorPosition.y - centerY, 2)
      );
      
      const isOverTarget = distance <= radius;
      
      if (isOverTarget) {
        if (!isDwelling) {
          setIsDwelling(true);
          dwellStartRef.current = Date.now();
        }
        
        const elapsed = Date.now() - dwellStartRef.current;
        const progress = Math.min((elapsed / 3000) * 100, 100);
        setLocalDwellProgress(progress);
        
        if (progress >= 100) {
          handleWake();
        }
      } else {
        setIsDwelling(false);
        setLocalDwellProgress(0);
        dwellStartRef.current = 0;
      }
    };
    
    const interval = setInterval(checkDwell, 50);
    return () => clearInterval(interval);
  }, [wakeBehavior, effectiveCursorPosition, isDwelling, handleWake]);

  // SELECT WAKE BEHAVIOR (also handles Enter/Space for all modes as fallback)
  useEffect(() => {
    const handleScanAction = (e: CustomEvent<{ action: string }>) => {
      if (wakeBehavior !== 'select') return;
      if (e.detail.action === 'select') {
        setSelectCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            handleWake();
          }
          return newCount;
        });
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Eye control has its own handler (handleEyeKey effect) that maps gestures
      // to actions via the user's action mapping. Skip raw Enter/Space counting
      // for eye_control to avoid double-counting or counting non-select gestures.
      const currentSettings = getUserSettings();
      if (currentSettings.controlMethod === 'eye_control') return;

      const isEnter = e.key === 'Enter' || e.key === 'NumpadEnter' || e.code === 'Enter' || e.code === 'NumpadEnter';
      const isSpace = e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space';

      if (isEnter || isSpace) {
        e.preventDefault();
        e.stopPropagation();

        if (wakeBehavior === 'select') {
          setSelectCount(prev => {
            const newCount = prev + 1;
            if (newCount >= 3) {
              handleWake();
            }
            return newCount;
          });
        } else {
          // For dwell/voice modes, single Enter exits immediately
          handleWake();
        }
      }
    };

    window.addEventListener('scanningAction', handleScanAction as EventListener);
    // Capture phase so we still receive the event even if other handlers stop propagation.
    window.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      window.removeEventListener('scanningAction', handleScanAction as EventListener);
      window.removeEventListener('keydown', handleKeyDown, { capture: true } as AddEventListenerOptions);
    };
  }, [wakeBehavior, handleWake]);

  // EYE CONTROL SELECT WAKE: The /rest page does not mount MainLayout so
  // useEyeControlInput is inactive. Handle eye-control arrow-key gestures
  // directly here so that any gesture configured as 'select' (not just blink/Enter)
  // can wake rest mode.
  useEffect(() => {
    const currentSettings = getUserSettings();
    if (wakeBehavior !== 'select' || currentSettings.controlMethod !== 'eye_control') return;

    const handleEyeKey = (e: KeyboardEvent) => {
      const action = getEyeControlAction(e);
      if (action === 'select') {
        e.preventDefault();
        e.stopPropagation();
        setSelectCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) handleWake();
          return newCount;
        });
      }
    };

    window.addEventListener('keydown', handleEyeKey, { capture: true });
    return () => window.removeEventListener('keydown', handleEyeKey, { capture: true } as AddEventListenerOptions);
  }, [wakeBehavior, handleWake]);

  // VOICE WAKE BEHAVIOR
  useEffect(() => {
    if (wakeBehavior !== 'voice') return;
    
    const handleVoiceNumber = (e: CustomEvent<{ number: number }>) => {
      if (e.detail.number === voiceNumber) {
        setVoiceCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            handleWake();
          }
          return newCount;
        });
      }
    };
    
    window.addEventListener('voiceNumber', handleVoiceNumber as EventListener);
    
    return () => {
      window.removeEventListener('voiceNumber', handleVoiceNumber as EventListener);
    };
  }, [wakeBehavior, voiceNumber, handleWake]);

  // POINTER WAKE BEHAVIOR — 3 clicks or taps anywhere on screen
  useEffect(() => {
    if (wakeBehavior !== 'pointer') return;

    const bump = () => {
      setSelectCount(prev => {
        const newCount = prev + 1;
        if (newCount >= 3) handleWake();
        return newCount;
      });
    };

    const handlePointerDown = (e: PointerEvent) => {
      // Only count primary mouse button or touch/pen
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      bump();
    };

    window.addEventListener('pointerdown', handlePointerDown, { capture: true });
    return () => window.removeEventListener('pointerdown', handlePointerDown, { capture: true } as AddEventListenerOptions);
  }, [wakeBehavior, handleWake]);

  // Escape key fallback
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleWake();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", handleKeyDown, { capture: true } as AddEventListenerOptions);
  }, [handleWake]);

  const getInstructionText = () => {
    const gender = settings.gender;
    const suffix = gender === 'female' ? 'Female' : 'Male';
    
    switch (wakeBehavior) {
      case 'dwell':
        return (t as any)(`restModeDwellInstruction${suffix}`) || (t as any)("restModeDwellInstruction") || "החזק מבט על העיגול למשך 3 שניות";
      case 'select':
        return (t as any)(`restModeSelectInstruction${suffix}`) || (t as any)("restModeSelectInstruction") || "בצע פעולת בחירה 3 פעמים";
      case 'pointer':
        return (t as any)(`restModePointerInstruction${suffix}`) || (t as any)("restModePointerInstruction") || "הקש בעכבר (או גע במסך) שלוש פעמים";
      case 'voice':
        return (t as any)(`restModeVoiceInstruction${suffix}`) || (t as any)("restModeVoiceInstruction") || `אמור "${voiceNumber}" שלוש פעמים`;
      default:
        return "";
    }
  };

  const fontScale = settings.fontSize === 'large' ? 1.25 : settings.fontSize === 'small' ? 0.85 : 1;

  return (
    <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black" style={{ fontSize: `${16 * fontScale}px` }}>
      {/* Moon Icon and Title */}
      <div className="text-center mb-8 pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Moon className="w-24 h-24 text-primary mx-auto mb-4" />
        </motion.div>

        <h1 className="font-bold text-white mb-2" style={{ fontSize: '2.25em' }}>{t("restMode")}</h1>
        <p className="text-white/60" style={{ fontSize: '1.25em' }}>{getInstructionText()}</p>
      </div>

      {/* Wake Target */}
      <div className="relative">
        {wakeBehavior === 'dwell' && (
          <div
            ref={wakeTargetRef}
            className="relative w-40 h-40 rounded-full border-4 border-primary/50 flex items-center justify-center cursor-pointer"
          >
            <svg className="absolute inset-0 w-full h-full -rotate-90">
              <circle
                cx="80"
                cy="80"
                r="76"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                className="text-primary"
                strokeDasharray={`${(localDwellProgress / 100) * 478} 478`}
                strokeLinecap="round"
              />
            </svg>
            <span className="text-white text-lg font-semibold">
              {Math.round(localDwellProgress)}%
            </span>
          </div>
        )}

        {(wakeBehavior === 'select' || wakeBehavior === 'pointer') && (
          <div className="flex gap-4">
            {[1, 2, 3].map((num) => (
              <div
                key={num}
                className={cn(
                  "w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all duration-300",
                  selectCount >= num
                    ? "bg-primary border-primary text-primary-foreground scale-110"
                    : "border-white/30 text-white/30"
                )}
              >
                <span className="text-2xl font-bold">{num}</span>
              </div>
            ))}
          </div>
        )}

        {wakeBehavior === 'voice' && (
          <div className="flex flex-col items-center gap-6">
            <div className="w-32 h-32 rounded-full bg-primary/20 border-4 border-primary flex items-center justify-center">
              <span className="text-6xl font-bold text-primary font-poppins">
                {voiceNumber}
              </span>
            </div>
            
            <div className="flex gap-4">
              {[1, 2, 3].map((num) => (
                <div
                  key={num}
                  className={cn(
                    "w-6 h-6 rounded-full transition-all duration-300",
                    voiceCount >= num ? "bg-primary scale-125" : "bg-white/30"
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Escape hint */}
      <p className="absolute bottom-8 text-white/30 text-sm">
        {t("pressEscapeToWake")}
      </p>
    </div>
  );
}
